import { message, Modal, Spin } from "antd";
import cuid from "cuid";
import { find, get, orderBy } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { CREATE_LINK } from "utils/api/graphql/mutations/link";
import { UPDATE_PROJECT } from "utils/api/graphql/mutations/projects";
import { UPDATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { PROCESS_CONFIGS } from "utils/api/graphql/queries/process-config";
import { PROJECT_BROKER_TRACER_ID } from "utils/api/graphql/queries/projects";
import { TRACER, TRACERS } from "utils/api/graphql/queries/tracers";
import {
  PROCESS_CONFIG_TYPES,
  PROCESS_TAGS,
  SCREENS,
  THEMES_PALETTE,
  TRACER_TYPES,
} from "utils/constants";
import {
  B2C_PROSPECT_LINK_TEMPLATE_BY_THEME,
  resolveB2cProspectLinkTemplate,
} from "./b2cProspectLinkTemplates";
import CopyOrSendLink from "./CopyOrSendLink";
import { getGenerateLinkFlowKey, isGenerateLinkOpen } from "./generateLinkOpen";
import ProcessPicker from "./ProcessPicker";

const STEPS = {
  LOADING: "LOADING",
  PICK_PROCESS: "PICK_PROCESS",
  DONE: "DONE",
};

const FALLBACK_TRACERS_TAKE = 80;

/**
 * Parent views usually build `baseUrl` from a theme-based tracer, which can
 * differ from the tracer selected for ASSOCIATED_PROCESSES / fallback. Sync the
 * `/init/{sourceId}` segment with the tracer actually used for the link.
 */
const replaceInitSourceInUrl = (baseUrl, sourceId) => {
  if (!baseUrl || !sourceId) {
    return baseUrl;
  }
  if (!/\/init\/[^/?#]+/.test(baseUrl)) {
    return baseUrl;
  }
  return baseUrl.replace(/(\/init\/)([^/?#]+)/, `$1${sourceId}`);
};

const uniqueB2cProcessesFromTracers = (tracersList) => {
  const map = new Map();
  tracersList.forEach((tr) => {
    const proc = get(tr, "flow.process");
    if (!proc?.id || map.has(proc.id)) {
      return;
    }
    map.set(proc.id, {
      id: proc.id,
      name: proc.name,
      theme: proc.theme,
    });
  });
  return [...map.values()];
};

const pickTracerForProcessId = (tracersList, processId) =>
  tracersList.find((tr) => get(tr, "flow.process.id") === processId);

const resolveLinkTargetStatusId = (statuses, template) => {
  const list = Array.isArray(statuses) ? statuses : [];
  if (list.length === 0) {
    return undefined;
  }
  if (template != null) {
    const byTemplate = find(list, (s) => s.template === template);
    if (byTemplate?.id) {
      return byTemplate.id;
    }
  }
  const byTag = find(list, { tag: PROCESS_TAGS.CURRENT_SUBSCRIPTION });
  if (byTag?.id) {
    return byTag.id;
  }
  const sorted = orderBy(
    list,
    [(s) => Number.parseInt(String(s.order), 10)],
    ["asc"],
  );
  return sorted[0]?.id;
};

const parseStatusOrder = (status) =>
  Number.parseInt(String(get(status, "order", 0)), 10);

const resolveStatusIdWithHigherOrder = (statuses, statusIdA, statusIdB) => {
  if (!statusIdA) {
    return statusIdB;
  }
  if (!statusIdB) {
    return statusIdA;
  }
  if (statusIdA === statusIdB) {
    return statusIdA;
  }
  const list = Array.isArray(statuses) ? statuses : [];
  const statusA = find(list, { id: statusIdA });
  const statusB = find(list, { id: statusIdB });
  const orderA = parseStatusOrder(statusA);
  const orderB = parseStatusOrder(statusB);
  return orderA >= orderB ? statusIdA : statusIdB;
};

const GenerateLinkModal = ({
  open,
  setOpen,
  template: processTemplate,
  tracerId,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const GENERATE_LINK = t("LIST.SUBSCRIPTION.GENERATE_SUBSCRIPTION_LINK", {
    returnObjects: true,
  });
  const resolutionErrorMessage = t(
    "LIST.SUBSCRIPTION.GENERATE_SUBSCRIPTION_LINK.RESOLUTION_ERROR",
  );
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState("");
  const [step, setStep] = useState(STEPS.LOADING);
  const [associatedProcesses, setAssociatedProcesses] = useState([]);
  /** Ref so fetching statuses does not recreate createLink → effect deps stay stable. */
  const projectStatusesRef = useRef([]);
  const [addLink] = useMutation(CREATE_LINK);
  const [updateProject] = useMutation(UPDATE_PROJECT, { onError: () => {} });
  const [updateSubscription] = useMutation(UPDATE_SUBSCRIPTION, {
    onError: () => {},
  });
  const [getTracer] = useLazyQuery(TRACER);
  const [getTracers] = useLazyQuery(TRACERS);
  const [getProcessConfigs] = useLazyQuery(PROCESS_CONFIGS);
  const [getProjectBrokerTracerId] = useLazyQuery(PROJECT_BROKER_TRACER_ID);
  const openFlowGenerationRef = useRef(0);
  const openRef = useRef(open);
  openRef.current = open;
  const getTracerRef = useRef(getTracer);
  const getProjectBrokerTracerIdRef = useRef(getProjectBrokerTracerId);
  const getProcessConfigsRef = useRef(getProcessConfigs);
  getTracerRef.current = getTracer;
  getProjectBrokerTracerIdRef.current = getProjectBrokerTracerId;
  getProcessConfigsRef.current = getProcessConfigs;

  const paletteProspectTheme =
    THEMES_PALETTE[processTemplate]?.PROSPECT_PROCESS;

  const onClose = () => {
    setOpen({});
    setStep(STEPS.LOADING);
    setAssociatedProcesses([]);
    setSelectedOption("");
    projectStatusesRef.current = [];
  };

  const createLink = useCallback(
    async (tracer, template, linkContext, fallbackProcessId) => {
      const statuses = get(tracer, "flow.process.processStatuses", []);
      const processId =
        get(tracer, "flow.process.id") || fallbackProcessId || undefined;
      const selectedStatus = resolveLinkTargetStatusId(statuses, template);
      const sourceId = get(tracer, "source.id");
      const url = replaceInitSourceInUrl(linkContext.baseUrl, sourceId);

      if (!processId || !sourceId) {
        message.error(resolutionErrorMessage);
        setStep(STEPS.LOADING);
        return;
      }

      if (linkContext.combinationId && linkContext.subscriptionId) {
        try {
          await updateSubscription({
            variables: {
              where: { id: linkContext.subscriptionId },
              data: {
                additionalInfo: {
                  combinationId: linkContext.combinationId,
                  complementaryInsurancePlanId:
                    linkContext.complementaryInsurancePlanId || null,
                },
              },
            },
          });

          if (linkContext.projectFields) {
            await updateProject({
              variables: {
                where: { id: linkContext.projectId },
                data: {
                  fields: {
                    ...linkContext.projectFields,
                    selectedCombinationId: linkContext.combinationId,
                    currentSubscription: linkContext.subscriptionId,
                  },
                },
              },
            });
          }
        } catch {
          message.error(resolutionErrorMessage);
          setStep(STEPS.LOADING);
          return;
        }
      }

      const existingWebProjectStatus = (projectStatusesRef.current || []).find(
        ({ status }) => get(status, "process.id") === processId,
      );
      const existingWebStatusId = get(existingWebProjectStatus, "status.id");
      const existingStatusOrder = parseStatusOrder(
        find(statuses, { id: existingWebStatusId }),
      );
      const selectedStatusOrder = parseStatusOrder(
        find(statuses, { id: selectedStatus }),
      );
      const skipProjectStatusUpdate =
        Boolean(existingWebStatusId) &&
        (!selectedStatus || selectedStatusOrder < existingStatusOrder);

      if (!skipProjectStatusUpdate && !selectedStatus) {
        message.error(resolutionErrorMessage);
        setStep(STEPS.LOADING);
        return;
      }

      // The link must point exactly to the resolved template target so the
      // prospect lands on the intended screen (e.g. JUSTIFICATIONS after sign),
      // even when it is "behind" the project's current status. The higher-order
      // logic below only protects the project status from regressing, not the
      // link target itself.
      const linkStatusId = selectedStatus || existingWebStatusId;

      if (!skipProjectStatusUpdate) {
        const newProjectStatusId = cuid();
        const baseStatuses = (projectStatusesRef.current || [])
          .filter(({ status }) => get(status, "process.id") !== processId)
          .map(({ id }) => ({ id }));
        const newStatuses = [
          ...baseStatuses,
          { id: newProjectStatusId, status: { id: selectedStatus } },
        ];

        try {
          await updateProject({
            variables: {
              where: { id: linkContext.projectId },
              data: { statuses: newStatuses },
            },
          });
        } catch {
          message.error(resolutionErrorMessage);
          setStep(STEPS.LOADING);
          return;
        }

        projectStatusesRef.current = [
          ...(projectStatusesRef.current || []).filter(
            ({ status }) => get(status, "process.id") !== processId,
          ),
          {
            id: newProjectStatusId,
            status: { id: selectedStatus, process: { id: processId } },
          },
        ];
      }

      addLink({
        variables: {
          data: {
            url,
            subscription: { id: linkContext.subscriptionId },
            project: { id: linkContext.projectId },
            fields: {
              process: { id: processId },
              status: { id: linkStatusId },
              ...(linkContext.combinationId && {
                combinationId: linkContext.combinationId,
                complementaryInsurancePlanId:
                  linkContext.complementaryInsurancePlanId || null,
              }),
            },
          },
        },
        onCompleted: ({ addLink: linkResult }) => {
          setOpen((prev) => ({
            ...prev,
            link: `${url}?l=${linkResult.id}&uid=${linkResult.uid}`,
          }));
          setStep(STEPS.DONE);
        },
      });
    },
    [
      addLink,
      resolutionErrorMessage,
      setOpen,
      updateProject,
      updateSubscription,
    ],
  );

  const generateLinkUsingLoadedTracer = useCallback(
    async (tracer, linkContext) => {
      const processTheme = get(tracer, "flow.process.theme");
      const templateEntry =
        B2C_PROSPECT_LINK_TEMPLATE_BY_THEME[processTheme] ??
        B2C_PROSPECT_LINK_TEMPLATE_BY_THEME[paletteProspectTheme];
      const template = resolveB2cProspectLinkTemplate(templateEntry, {
        isSigned: linkContext.isSigned,
        isInfoCompleted: linkContext.isInfoCompleted,
      });
      setSelectedOption(template || "");
      await createLink(
        tracer,
        template,
        linkContext,
        get(tracer, "flow.process.id"),
      );
    },
    [createLink, paletteProspectTheme],
  );

  const loadTracerForAssociatedB2cProcess = useCallback(
    async (processRef) => {
      const processId = processRef?.id;
      const processTheme = processRef?.theme;
      if (!processId) {
        return null;
      }

      const primary = await getTracer({
        variables: {
          where: { flow: { process: { id: processId } } },
        },
        fetchPolicy: "network-only",
      });
      let tracer = primary.data?.tracer;
      if (!tracer && processTheme) {
        const { data } = await getTracers({
          variables: {
            where: {
              flow: {
                process: {
                  theme: processTheme,
                },
              },
            },
            take: FALLBACK_TRACERS_TAKE,
          },
          fetchPolicy: "network-only",
        });
        const list = get(data?.tracers, "data", []);
        tracer = pickTracerForProcessId(list, processId);
      }

      return tracer;
    },
    [getTracer, getTracers],
  );

  const generateLinkForProcess = useCallback(
    async (processRef, linkContext) => {
      const tracer = await loadTracerForAssociatedB2cProcess(processRef);
      if (!tracer) {
        message.error(resolutionErrorMessage);
        setStep(STEPS.LOADING);
        return;
      }

      await generateLinkUsingLoadedTracer(tracer, linkContext);
    },
    [
      loadTracerForAssociatedB2cProcess,
      generateLinkUsingLoadedTracer,
      resolutionErrorMessage,
    ],
  );

  const generateLinkFallback = useCallback(
    async (linkContext) => {
      const correspondingTheme = paletteProspectTheme;
      const template = resolveB2cProspectLinkTemplate(
        B2C_PROSPECT_LINK_TEMPLATE_BY_THEME[correspondingTheme],
        {
          isSigned: linkContext.isSigned,
          isInfoCompleted: linkContext.isInfoCompleted,
        },
      );
      setSelectedOption(template || "");

      const { data } = await getTracers({
        variables: {
          where: {
            flow: {
              process: {
                theme: correspondingTheme,
              },
            },
          },
          take: FALLBACK_TRACERS_TAKE,
        },
        fetchPolicy: "network-only",
      });
      const tracersList = get(data?.tracers, "data", []);
      if (tracersList.length === 0) {
        message.error(resolutionErrorMessage);
        setStep(STEPS.LOADING);
        return;
      }

      const uniqueProcesses = uniqueB2cProcessesFromTracers(tracersList);
      if (uniqueProcesses.length === 0) {
        message.error(resolutionErrorMessage);
        setStep(STEPS.LOADING);
        return;
      }

      if (uniqueProcesses.length === 1) {
        const tracer = pickTracerForProcessId(
          tracersList,
          uniqueProcesses[0].id,
        );
        if (tracer) {
          await createLink(
            tracer,
            template,
            linkContext,
            uniqueProcesses[0].id,
          );
        } else {
          message.error(resolutionErrorMessage);
          setStep(STEPS.LOADING);
        }
        return;
      }

      const defaultTracers = tracersList.filter((tr) => tr.isDefault);
      const defaultProcessIds = [
        ...new Set(
          defaultTracers
            .map((tr) => get(tr, "flow.process.id"))
            .filter(Boolean),
        ),
      ];
      if (defaultProcessIds.length === 1) {
        const tracer = pickTracerForProcessId(
          tracersList,
          defaultProcessIds[0],
        );
        if (tracer) {
          await createLink(tracer, template, linkContext, defaultProcessIds[0]);
          return;
        }
      }

      setAssociatedProcesses(uniqueProcesses);
      setStep(STEPS.PICK_PROCESS);
    },
    [paletteProspectTheme, getTracers, createLink, resolutionErrorMessage],
  );

  const onSelectProcess = useCallback(
    (proc) => {
      setStep(STEPS.LOADING);
      void generateLinkForProcess(proc, {
        baseUrl: open?.baseUrl,
        projectId: open?.projectId,
        subscriptionId: open?.subscriptionId,
        combinationId: open?.combinationId,
        complementaryInsurancePlanId: open?.complementaryInsurancePlanId,
        projectFields: open?.projectFields,
        isSigned: open?.isSigned,
        isInfoCompleted: open?.isInfoCompleted,
      });
    },
    [generateLinkForProcess, open],
  );

  const generateLinkUsingLoadedTracerRef = useRef(
    generateLinkUsingLoadedTracer,
  );
  const generateLinkFallbackRef = useRef(generateLinkFallback);
  const generateLinkForProcessRef = useRef(generateLinkForProcess);
  generateLinkUsingLoadedTracerRef.current = generateLinkUsingLoadedTracer;
  generateLinkFallbackRef.current = generateLinkFallback;
  generateLinkForProcessRef.current = generateLinkForProcess;

  const openKey = getGenerateLinkFlowKey(open, tracerId);

  useEffect(() => {
    if (!openKey) return;

    const currentOpen = openRef.current;
    openFlowGenerationRef.current += 1;
    const generation = openFlowGenerationRef.current;

    setStep(STEPS.LOADING);

    const linkContext = {
      baseUrl: currentOpen.baseUrl,
      projectId: currentOpen.projectId,
      subscriptionId: currentOpen.subscriptionId,
      combinationId: currentOpen.combinationId,
      complementaryInsurancePlanId: currentOpen.complementaryInsurancePlanId,
      projectFields: currentOpen.projectFields,
      isSigned: currentOpen.isSigned,
      isInfoCompleted: currentOpen.isInfoCompleted,
    };

    const run = async () => {
      if (generation !== openFlowGenerationRef.current) {
        return;
      }

      const initialStatuses = currentOpen.initialProjectStatuses;
      const canSkipProjectStatusesQuery =
        Boolean(tracerId) && Array.isArray(initialStatuses);

      let tracerData;

      if (canSkipProjectStatusesQuery) {
        projectStatusesRef.current = [...initialStatuses];
        const tracerResult = await getTracerRef.current({
          variables: { where: { id: tracerId } },
          fetchPolicy: "network-only",
        });
        if (generation !== openFlowGenerationRef.current) {
          return;
        }
        tracerData = tracerResult.data;
      } else if (tracerId) {
        const [projectResult, tracerResult] = await Promise.all([
          getProjectBrokerTracerIdRef.current({
            variables: { where: { id: currentOpen.projectId } },
            fetchPolicy: "network-only",
          }),
          getTracerRef.current({
            variables: { where: { id: tracerId } },
            fetchPolicy: "network-only",
          }),
        ]);
        if (generation !== openFlowGenerationRef.current) {
          return;
        }
        projectStatusesRef.current = get(
          projectResult.data,
          "project.statuses",
          [],
        );
        tracerData = tracerResult.data;
      } else {
        const { data: projectData } = await getProjectBrokerTracerIdRef.current(
          {
            variables: { where: { id: currentOpen.projectId } },
            fetchPolicy: "network-only",
          },
        );
        if (generation !== openFlowGenerationRef.current) {
          return;
        }
        projectStatusesRef.current = get(projectData, "project.statuses", []);

        const resolvedTracerIdFromProject = get(
          projectData,
          "project.tracer.id",
        );

        if (!resolvedTracerIdFromProject) {
          await generateLinkFallbackRef.current(linkContext);
          return;
        }

        tracerData = (
          await getTracerRef.current({
            variables: { where: { id: resolvedTracerIdFromProject } },
            fetchPolicy: "network-only",
          })
        ).data;
      }

      if (generation !== openFlowGenerationRef.current) {
        return;
      }

      const resolvedTracerId = tracerId || get(tracerData, "tracer.id");

      if (resolvedTracerId) {
        const contextTracer = tracerData?.tracer;
        const usesContextTracerDirectly =
          contextTracer?.type && contextTracer.type !== TRACER_TYPES.MANUAL;

        if (usesContextTracerDirectly) {
          await generateLinkUsingLoadedTracerRef.current(
            contextTracer,
            linkContext,
          );
          return;
        }

        const { data } = await getProcessConfigsRef.current({
          variables: {
            where: {
              type: PROCESS_CONFIG_TYPES.ASSOCIATED_PROCESSES,
              process: {
                flows: { tracers: { id: resolvedTracerId } },
              },
            },
          },
          fetchPolicy: "network-only",
        });
        if (generation !== openFlowGenerationRef.current) {
          return;
        }
        const configs = get(data?.processConfigs, "data", []);
        const processes = get(configs, "[0].value.processes", []);

        if (processes.length === 0) {
          await generateLinkFallbackRef.current(linkContext);
        } else if (processes.length === 1) {
          await generateLinkForProcessRef.current(processes[0], linkContext);
        } else {
          setAssociatedProcesses(processes);
          setStep(STEPS.PICK_PROCESS);
        }
      } else {
        await generateLinkFallbackRef.current(linkContext);
      }
    };

    void run();
  }, [openKey, tracerId]);

  const onCopyLink = () => {
    navigator.clipboard.writeText(open?.link);
    message.success(GENERATE_LINK.COPIED);
    setOpen((prev) => ({ ...prev, copied: true }));
  };

  const onSendLink = () => {
    navigate(SCREENS.PRIVATE.BROKER.SUBSCRIPTIONS.SEND_LINK.path, {
      state: open,
    });
  };

  const isOpen = isGenerateLinkOpen(open);

  const pickProcessTitle =
    GENERATE_LINK.PICK_PROCESS_TITLE || "Choisir un parcours";
  const modalTitle =
    step === STEPS.PICK_PROCESS
      ? pickProcessTitle
      : GENERATE_LINK[selectedOption]?.TITLE || GENERATE_LINK.TITLE;

  const renderContent = () => {
    if (step === STEPS.PICK_PROCESS) {
      return (
        <ProcessPicker
          processes={associatedProcesses}
          onSelect={onSelectProcess}
        />
      );
    }

    if (step === STEPS.DONE) {
      return (
        <CopyOrSendLink
          onCopyLink={onCopyLink}
          onSendLink={onSendLink}
          link={open?.link}
          selectedOption={selectedOption}
          linkCopied={open?.copied}
        />
      );
    }

    return (
      <div className="d-flex justify--center p-24">
        <Spin />
      </div>
    );
  };

  return (
    <Modal
      title={modalTitle}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      className="generate-link-modal"
    >
      {renderContent()}
    </Modal>
  );
};

export default GenerateLinkModal;
